import "./project-place.css";

import type { Reporter } from "idfweb-tasks";

import { formatBytes, type KeptProjects } from "./kept-projects.ts";
import { localDirectoriesSupported, pickDirectory } from "./local-directory.ts";
import type { ProjectRecord } from "./project-records.ts";
import { browserStorageSupported, savedTreeBytes } from "./saved-tree.ts";
import { askWhereToKeep } from "./sync-dialogs.ts";

/**
 * The toolbar's button that shows the open project's name and where it is
 * kept, and moves it to the other place. See "Moving a Project" in the page's
 * README.
 */
export function createPlaceButton(
    kept: KeptProjects,
    reporter: Reporter,
): { element: HTMLButtonElement; show(record: ProjectRecord | undefined): void } {
    const element = document.createElement("button");
    element.type = "button";
    element.className = "idfweb-device-button idfweb-place";
    element.hidden = true;
    const glyph = document.createElement("span");
    glyph.setAttribute("aria-hidden", "true");
    const label = document.createElement("span");
    label.className = "idfweb-place-name";
    const chevron = document.createElement("span");
    chevron.className = "codicon codicon-chevron-down";
    chevron.setAttribute("aria-hidden", "true");
    element.append(glyph, label, chevron);

    let moving = false;
    const show = (record: ProjectRecord | undefined) => {
        element.hidden = record === undefined;
        if (record === undefined) return;
        glyph.className = `codicon codicon-${record.kind === "browser" ? "browser" : "folder"}`;
        label.textContent = record.name;
        element.title =
            record.kind === "browser"
                ? `${record.name}: stored in the browser`
                : `${record.name}: stored in the local folder ${record.handle?.name ?? record.tree}`;
        element.setAttribute("aria-label", element.title);
        element.disabled = moving;
    };

    const move = async (record: ProjectRecord) => {
        const place = await askWhereToKeep(record.name, record.kind, {
            tree: record.tree,
            browser: browserStorageSupported(),
            folder: localDirectoriesSupported(),
            folderName: record.handle?.name,
            size:
                record.kind === "browser"
                    ? savedTreeBytes(record.id).then(
                          (bytes) => (bytes === undefined ? undefined : formatBytes(bytes)),
                          () => undefined,
                      )
                    : undefined,
        });
        if (place === undefined || place === record.kind) return;
        // The picker needs the click on Move, so it opens before anything else is awaited.
        const parent = place === "folder" ? await pickDirectory(true) : undefined;
        if (place === "folder" && parent === undefined) return;
        moving = true;
        show(kept.current());
        try {
            const why = parent !== undefined ? await kept.moveToFolder(parent) : await kept.moveToBrowser();
            if (why !== undefined) reporter.message("error", `Could not move ${record.name}: ${why}`);
            else if (parent !== undefined) {
                reporter.message("info", `${record.name} moved to ${parent.name}/${record.tree}.`);
            } else reporter.message("info", `${record.name} moved to browser storage.`);
        } finally {
            moving = false;
            show(kept.current());
        }
    };

    element.addEventListener("click", () => {
        const record = kept.current();
        if (record === undefined || moving) return;
        move(record).catch((e: unknown) =>
            reporter.message("error", `Could not move ${record.name}: ${e instanceof Error ? e.message : String(e)}`),
        );
    });
    return { element, show };
}
